import { City } from 'hotdesk_typescript';
import { buildLocationPagePath } from '@/hotdesk-ui/helpers/workspace-url';

type CityWithRegion = Pick<City, 'name' | 'slug' | 'country'> & {
    region?: { name?: string | null; slug?: string | null } | null;
};

export interface LocationCrumb {
    name: string;
    /** Site-relative path without a leading slash. */
    path?: string;
}

const same = (a?: string | null, b?: string | null) =>
    !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * The city's state / governorate (`workspaceCity.region`), or undefined when
 * the API has none (Hong Kong) or it only repeats the city ("Cairo, Cairo").
 */
export function getRegionName(
    city: CityWithRegion | null | undefined
): string | undefined {
    const region = city?.region?.name?.trim();
    if (!region || same(region, city?.name)) return undefined;
    return region;
}

/**
 * The state landing page for the city's region (workspaces/{country}/{state}),
 * or undefined when the city has no state of its own (see `getRegionName`) or
 * its country has no state pages (only the US and Canada do).
 */
export function buildRegionPagePath(
    city: CityWithRegion | null | undefined
): string | undefined {
    if (!getRegionName(city) || !city?.country) return undefined;
    return buildLocationPagePath({ country: city.country, region: city.region }) || undefined;
}

/** "New Cairo, Cairo" — the city plus its state, for titles and copy. */
export function cityWithRegion(
    city: CityWithRegion | null | undefined
): string {
    return [city?.name, getRegionName(city)].filter(Boolean).join(', ');
}

/**
 * City and state for a page title, minus any part the name already says
 * ("Regus Cairo Festival City" → just the state), so titles don't repeat.
 */
export function placeForTitle(
    name: string | null | undefined,
    city: CityWithRegion | null | undefined
): string {
    const lowerName = (name ?? '').toLowerCase();
    return [city?.name, getRegionName(city)]
        .filter((part): part is string => !!part)
        .filter((part) => !lowerName.includes(part.trim().toLowerCase()))
        .join(', ');
}

/**
 * Country → State → City, each linked to its SEO landing page
 * (workspaces/…). Same levels as the page's BreadcrumbList JSON-LD.
 */
export function buildLocationTrail(
    city: CityWithRegion | null | undefined
): LocationCrumb[] {
    const crumbs: LocationCrumb[] = [];
    const country = city?.country;

    if (country?.name)
        crumbs.push({ name: country.name, path: buildLocationPagePath({ country }) });

    const regionName = getRegionName(city);
    const regionPath = buildRegionPagePath(city);
    if (regionName && regionPath) crumbs.push({ name: regionName, path: regionPath });

    if (city?.name)
        crumbs.push({
            name: city.name,
            path: buildLocationPagePath({ country, city })
        });

    return crumbs;
}
